import { BadgeCheck, ChevronDown, CreditCard, LogOut, Sparkles, UserIcon } from "lucide-react"
import { Link } from "react-router-dom"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/lib/auth.tsx"
import { supabase } from "@/lib/supabase"

export function UserMenu() {
  const { session } = useAuth()
  const user = session?.user

  if (!user) return null
  
  // Function to clear all auth data
  const clearAuthData = () => {
    // Clear all Supabase auth data
    for (const key in localStorage) {
      if (key.includes('supabase.auth') || key.includes('sb-')) {
        localStorage.removeItem(key)
      }
    }
    // Also clear cookies related to auth
    document.cookie.split(';').forEach(cookie => {
      const [name] = cookie.trim().split('=')
      if (name.includes('sb-')) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
      }
    })
  }
  
  const handleSignOut = async () => {
    try {
      // Check if session exists before signing out
      const { data } = await supabase.auth.getSession()
      
      if (data.session) {
        await supabase.auth.signOut({ scope: 'global' })
      }
      
      // Clear all auth data
      clearAuthData()
      
      // Navigate to home with forceLogout parameter
      window.location.href = '/?forceLogout=' + Date.now()
    } catch (error) {
      console.error('Error signing out:', error)
      clearAuthData()
      window.location.href = '/?forceLogout=' + Date.now()
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2 px-2">
          <Avatar className="h-8 w-8 rounded-lg">
            <AvatarImage src={user.user_metadata?.avatar_url} alt={user.user_metadata?.full_name || user.email} />
            <AvatarFallback className="rounded-lg">
              <UserIcon className="h-5 w-5" />
            </AvatarFallback>
          </Avatar>
          <div className="truncate hidden md:block">{user.user_metadata?.full_name || user.email}</div>
          <ChevronDown className="ml-auto size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
        align="end"
        sideOffset={4}>
        <DropdownMenuLabel className="p-0 font-normal">
          <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
            <Avatar className="h-8 w-8 rounded-lg">
              <AvatarImage src={user.user_metadata?.avatar_url} alt={user.user_metadata?.full_name || user.email} />
              <AvatarFallback className="rounded-lg">
                <UserIcon className="h-5 w-5" />
              </AvatarFallback>
            </Avatar>
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span className="truncate font-semibold">{user.user_metadata?.full_name || user.email}</span>
              <span className="truncate text-xs text-muted-foreground">{user.email}</span>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/settings/subscription">
              <Sparkles className="size-4 me-2" />
              Upgrade
            </Link>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/profile">
              <BadgeCheck className="size-4 me-2" />
              Account
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/settings/billing">
              <CreditCard className="size-4 me-2" />
              Billing
            </Link>
          </DropdownMenuItem>
          {/* <DropdownMenuItem asChild>
            <Link to="/notifications">
              <Bell className="size-4 me-2" />
              Notifications
            </Link>
          </DropdownMenuItem> */}
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-red-700 cursor-pointer" onClick={handleSignOut}>
          <LogOut className="size-4 me-2" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
} 